/**
 * Coach suggestions — mixed list of recommended next sessions powering the
 * learner dashboard's "Suggested sessions" widget.
 *
 * Each suggestion is tagged with a `nudge` type that says WHY it's being
 * surfaced, and targets either a specific lesson (learn path) or a
 * curriculum node (revise path). The target type drives which session flow
 * the client launches when the row is clicked.
 */
import { z } from 'zod';
import { extendZodWithOpenApi } from '@asteasolutions/zod-to-openapi';
extendZodWithOpenApi(z);
/**
 * Why this suggestion exists. Drives the coloured pill on the row and
 * gates which session type is launched.
 *
 * - `start` — untouched lesson in the learner's library. Click → learn session.
 * - `refresh` — subject at confident/mastered, decay setting in. Click → revise.
 * - `breakthrough` — subject where accuracy has recently dropped. Click → revise.
 * - `push` — subject plateau'd at practised/confident. Click → revise (harder).
 * - `build` — touched subject with low response volume. Click → revise.
 */
export const NudgeTypeSchema = z
    .enum(['start', 'refresh', 'breakthrough', 'push', 'build'])
    .openapi('NudgeType', {
    description: "Why the suggestion was surfaced — drives the row's nudge-type pill.",
    example: 'refresh',
});
/**
 * Lesson target — start a learn session for this lesson. Covers both
 * course lessons (which need a `courseId` for routing) and personal
 * lessons (which are standalone).
 */
export const LessonTargetSchema = z
    .object({
    type: z.literal('lesson'),
    lessonId: z.string().uuid(),
    kind: z.enum(['course', 'personal']),
    /** Required when kind = 'course' for routing to /courses/:courseId/:lessonId. */
    courseId: z.string().uuid().optional(),
})
    .openapi('LessonTarget');
/**
 * Subject target — start a revise session scoped to this curriculum node
 * (and its descendants, per the existing session builder).
 */
export const SubjectTargetSchema = z
    .object({
    type: z.literal('subject'),
    nodeId: z.string().uuid(),
})
    .openapi('SubjectTarget');
export const SuggestionTargetSchema = z
    .discriminatedUnion('type', [LessonTargetSchema, SubjectTargetSchema])
    .openapi('SuggestionTarget');
export const SuggestionSchema = z
    .object({
    id: z.string().openapi({
        description: 'Stable key for the suggestion (derived from nudge + target).',
        example: 'refresh:550e8400-e29b-41d4-a716-446655440000',
    }),
    nudge: NudgeTypeSchema,
    title: z.string().openapi({
        description: 'Display title — lesson title for lesson targets, topic name for topics.',
        example: 'Cardiac Arrhythmias',
    }),
    path: z.string().openapi({
        description: 'Breadcrumb path to the lesson/topic — either the curriculum breadcrumb or a course > module > topic path for lessons.',
        example: 'Medicine › Cardiology',
    }),
    reason: z.string().openapi({
        description: 'One-line human explanation of why this was suggested. Shown under the title.',
        example: 'You reached confident 2 weeks ago. A short session now keeps it sharp.',
    }),
    estMinutes: z.number().openapi({
        description: 'Estimated session length in minutes.',
        example: 8,
    }),
    target: SuggestionTargetSchema,
    priority: z.number().openapi({
        description: 'Scoring used by the coach service for ordering. Higher = more urgent. Exposed so the web layer can re-sort after client-side filtering.',
        example: 80,
    }),
})
    .openapi('Suggestion');
export const SuggestionsResponseSchema = z
    .object({
    suggestions: z.array(SuggestionSchema),
})
    .openapi('SuggestionsResponse');
export const SuggestionsQuerySchema = z
    .object({
    limit: z.coerce.number().int().min(1).max(20).default(10),
    /** Recency window for the "new lessons" / "accuracy dip" signals, in days. */
    windowDays: z.coerce.number().int().min(1).max(365).default(7),
})
    .openapi('SuggestionsQuery');
export const DismissSuggestionBodySchema = z
    .object({
    suggestionId: z.string().min(1).openapi({
        description: 'The `id` from a previously-returned Suggestion. Deterministic server-side (e.g. "refresh:<uuid>") so repeat dismissals upsert cleanly.',
        example: 'refresh:550e8400-e29b-41d4-a716-446655440000',
    }),
    /**
     * Optional override for how long the dismissal stays active. Defaults to
     * 2 hours server-side — long enough that a single session doesn't
     * re-surface the same suggestion, short enough that persistent problems
     * come back the same day.
     */
    ttlSeconds: z.coerce.number().int().min(60).max(86400).optional().openapi({
        description: 'Override the default 2-hour dismissal TTL (60–86400 seconds).',
        example: 7200,
    }),
})
    .openapi('DismissSuggestionBody');
import { MasteryLevelSchema } from './stats.schema.js';
/**
 * One row in the user's subjects snapshot — a top-level domain (always
 * present once snapshot has run) or a sub-domain qualifying via the rollup
 * rule in `userSubjects.buildSubjects`.
 */
export const SubjectSnapshotSchema = z
    .object({
    nodeId: z.string().uuid(),
    parentNodeId: z.string().uuid().nullable().openapi({
        description: 'Curriculum parent node id. Null for top-level domains. ' +
            'Always points at another subject IN THIS SAME RESPONSE — the rollup ' +
            'guarantees that if a non-domain subject is emitted, its curriculum ' +
            'parent is too. The frontend uses this to render the snapshot as a ' +
            'tree without a second round-trip.',
    }),
    displayName: z.string(),
    breadcrumb: z.array(z.string()).openapi({
        description: 'Top-down ancestor display-name chain. Empty for top-level domains.',
    }),
    score: z.number().min(0).max(1).openapi({
        description: 'Mastery score 0..1. Display as percent.',
    }),
    level: MasteryLevelSchema.openapi({
        description: 'Derived on read via scoreToLevel(score, responseCount).',
    }),
    responseCount: z.number().int().min(0),
    userQuestionCount: z.number().int().min(0).openapi({
        description: 'Revisable questions for this user under this subject.',
    }),
    lastResponseAt: z.string().datetime().nullable(),
    isDomain: z.boolean(),
})
    .openapi('SubjectSnapshot');
/**
 * Library distribution payload — drives the dashboard's "Your Library"
 * section: 5-segment journey bar, subjects list, and horizon count.
 *
 * Replaces the old aggregate-mastered headline (masteredPct/masteredDelta)
 * with the persisted subjects snapshot, surfaced via the worker job in
 * @formency/worker/processors/subjectsSnapshotProcessor.
 */
export const LibraryDistributionSchema = z
    .object({
    unstarted: z.number().int().openapi({
        description: 'Subjects with zero responses.',
        example: 32,
    }),
    introduced: z.number().int().openapi({
        description: 'Subjects at the "first look" level.',
        example: 18,
    }),
    practised: z.number().int().openapi({
        description: 'Subjects at the "making progress" level.',
        example: 14,
    }),
    confident: z.number().int().openapi({
        description: 'Subjects at the "confident" level.',
        example: 11,
    }),
    mastered: z.number().int().openapi({
        description: 'Subjects at the "mastered" level.',
        example: 12,
    }),
    total: z.number().int().openapi({
        description: 'Total subject count in the library.',
        example: 87,
    }),
    started: z.number().int().openapi({
        description: 'Subjects the learner has at least one response on.',
        example: 55,
    }),
    /**
     * One row per (user, subject) — top-level domains plus qualifying
     * sub-subjects per the rollup. Persisted in user_subject_snapshots.
     */
    subjects: z.array(SubjectSnapshotSchema),
    /**
     * Additional subjects the user would track if they completed all
     * lessons in courses they're enrolled in (plus all personal lessons).
     */
    horizon: z.number().int().min(0).openapi({
        description: 'Subjects waiting in unfinished enrolled lessons.',
        example: 12,
    }),
    /**
     * True when last_session_completed_at advanced past
     * subjects_computed_at (i.e. the snapshot is behind a recent
     * completion). The frontend polls until this clears.
     */
    stale: z.boolean(),
    lastComputedAt: z.string().datetime().nullable().openapi({
        description: 'Null on the first load before any snapshot has been computed.',
    }),
})
    .openapi('LibraryDistribution');
//# sourceMappingURL=suggestion.schema.js.map